import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { CompanyContext } from '../contexts/CompanyContext';
import {
  Building2, LayoutDashboard, FileText, Wrench, ClipboardList, Users, Package,
  ShoppingCart, Warehouse, UserCheck, FileCheck, Activity, Sparkles, Settings,
  LogOut, Menu, X, Home, MoreHorizontal, ChevronRight, BarChart3, Phone,
  Calendar, Bell, BookOpen, ClipboardCheck, FileBarChart, ShieldCheck, Database, History,
  Wallet, UserCog, Crown, FolderOpen, GitBranch, TrendingUp, StickyNote,
  CalendarDays, FileSignature, Receipt, Truck, FileSearch,
  AlertTriangle, Footprints, UserPlus, KeyRound, Hammer, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Camera } from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import NotificationBell from './NotificationBell';
import TaskIconButton from './TaskIconButton';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import BrandLogo from './BrandLogo';
import TrialBanner from './TrialBanner';
import { useT } from '../contexts/LanguageContext';

// Role label keys (translated via t())
const ROLE_KEYS = {
  SUPERADMIN: 'role.SUPERADMIN',
  FULL_ACCESS: 'role.FULL_ACCESS',
  ADMIN: 'role.ADMIN',
  CEO: 'role.CEO',
  MANAGER: 'role.MANAGER',
  ACCOUNTANT: 'role.ACCOUNTANT',
  PROCUREMENT_OFFICER: 'role.PROCUREMENT_OFFICER',
  HR_MANAGER: 'role.HR_MANAGER',
  STOREKEEPER: 'role.STOREKEEPER',
  SECURITY_OFFICER: 'role.SECURITY_OFFICER',
  CS_SUPERVISOR: 'role.CS_SUPERVISOR',
  ENGINEER: 'role.ENGINEER',
  TECHNICIAN: 'role.TECHNICIAN',
  TENANT: 'role.TENANT'
};

// Menu groups with color themes. Each group gets a distinct accent color
// shown on the section header, active-item ring, and group-icon background.
export const MENU_GROUPS = {
  overview:           { label: 'Overview',              tKey: 'group.overview',           color: 'sky',      hex: '#0284c7' },
  complaint_workflow: { label: 'Complaint Workflow',    tKey: 'group.complaintWorkflow',  color: 'teal',     hex: '#0d9488' },
  operations:         { label: 'Operations',            tKey: 'group.operations',         color: 'amber',    hex: '#f0b43c' },
  security:    { label: 'Security',       tKey: 'group.security',    color: 'indigo',   hex: '#4338ca' },
  people:      { label: 'People & HR',    tKey: 'group.people',      color: 'violet',   hex: '#7c3aed' },
  finance:     { label: 'Finance & Accounts', tKey: 'group.finance', color: 'emerald',  hex: '#059669' },
  procurement: { label: 'Procurement',    tKey: 'group.procurement', color: 'cyan',     hex: '#0891b2' },
  compliance:  { label: 'Compliance',     tKey: 'group.compliance',  color: 'rose',     hex: '#e11d48' },
  insights:    { label: 'Insights',       tKey: 'group.insights',    color: 'fuchsia',  hex: '#c026d3' },
  admin:       { label: 'Administration', tKey: 'group.admin',       color: 'slate',    hex: '#475569' },
};

const ALL_MENU_ITEMS = [
  // Overview
  { path: '/dashboard', label: 'Dashboard', tKey: 'nav.dashboard', icon: LayoutDashboard, roles: ['*'], group: 'overview' },  { path: '/superadmin', label: 'Super Admin Dashboard', tKey: 'nav.superadmin', icon: Crown, roles: ['SUPERADMIN'], group: 'overview' },
  { path: '/my-work', label: 'My Work / Technician KPI', tKey: 'nav.myWork', icon: ClipboardCheck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER', 'CS_SUPERVISOR', 'TECHNICIAN'], group: 'overview' },
  { path: '/superadmin/onboard-company', label: 'Onboard New Company', tKey: 'nav.saOnboard', icon: UserPlus, roles: ['SUPERADMIN'], group: 'overview' },
  { path: '/my-notes', label: 'Notes & Reminders', tKey: 'nav.notes', icon: StickyNote, roles: ['*'], group: 'overview' },
  { path: '/notices', label: 'Notices', tKey: 'nav.notices', icon: Bell, roles: ['*'], group: 'overview' },
  { path: '/contacts', label: 'Contacts', tKey: 'nav.contacts', icon: Phone, roles: ['*'], group: 'overview' },

  // ── Complaint workflow (Feb-2026) — everything a complaint touches, in
  //    the order it flows: Complaints → Work Orders → Material Requests →
  //    Approvals → Service Reports → History (Pending Returns / audit).
  { path: '/complaints', label: 'Complaints', tKey: 'nav.complaints', icon: FileText, roles: ['*'], group: 'complaint_workflow' },
  { path: '/work-orders', label: 'Work Orders', tKey: 'nav.workOrders', icon: ClipboardCheck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN', 'ACCOUNTANT'], group: 'complaint_workflow' },
  { path: '/reports', label: 'Service Reports', tKey: 'nav.serviceReports', icon: FileBarChart, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN', 'STOREKEEPER'], group: 'complaint_workflow' },
  { path: '/material-requests', label: 'Material Requests', tKey: 'nav.materialRequests', icon: Package, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER', 'CS_SUPERVISOR', 'STOREKEEPER', 'TECHNICIAN'], group: 'complaint_workflow' },
  { path: '/approvals', label: 'Approvals', tKey: 'nav.approvals', icon: ClipboardCheck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'STOREKEEPER', 'TECHNICIAN'], group: 'complaint_workflow' },
  { path: '/pending-returns', label: 'History (Pending Returns)', tKey: 'nav.pendingReturns', icon: History, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'STOREKEEPER', 'ENGINEER', 'TECHNICIAN'], group: 'complaint_workflow' },

  // Operations
  { path: '/buildings', label: 'Buildings', tKey: 'nav.buildings', icon: Building2, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'], group: 'operations' },
  { path: '/assets', label: 'Assets', tKey: 'nav.assets', icon: Wrench, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER', 'TECHNICIAN'], group: 'operations' },
  { path: '/inspections', label: 'Inspections & Snagging', tKey: 'nav.inspections', icon: FileSearch, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'SECURITY_OFFICER'], group: 'operations' },
  { path: '/checklist-templates', label: 'Checklist Templates', tKey: 'nav.checklistTemplates', icon: ClipboardList, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'], group: 'operations' },
  { path: '/purchase-requests', label: 'Purchase Requests', tKey: 'nav.purchaseRequests', icon: ShoppingCart, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER', 'STOREKEEPER', 'PROCUREMENT_OFFICER'], group: 'procurement' },
  { path: '/ppm-tasks', label: 'PPM Tasks', tKey: 'nav.ppm', icon: ClipboardList, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN'], group: 'operations' },
  { path: '/schedules', label: 'Schedules', tKey: 'nav.schedules', icon: Calendar, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN', 'SECURITY_OFFICER'], group: 'operations' },
  { path: '/sops', label: 'SOPs & SLAs', tKey: 'nav.sops', icon: BookOpen, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN'], group: 'operations' },
  { path: '/utilities', label: 'Utilities', tKey: 'nav.utilities', icon: Activity, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER'], group: 'operations' },
  { path: '/ppm-calendar', label: 'PPM Calendar', tKey: 'nav.ppmCalendar', icon: CalendarDays, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN'], group: 'operations' },
  { path: '/housekeeping', label: 'Housekeeping', tKey: 'nav.housekeeping', icon: Sparkles, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'STOREKEEPER'], group: 'operations' },
  { path: '/job-orders', label: 'Job Orders', tKey: 'nav.jobOrders', icon: Hammer, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'PROCUREMENT_OFFICER'], group: 'operations' },

  // Security
  { path: '/security/incidents', label: 'Incidents', tKey: 'nav.secIncidents', icon: AlertTriangle, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'SECURITY_OFFICER'], group: 'security' },
  { path: '/security/patrols', label: 'Patrol Log', tKey: 'nav.secPatrols', icon: Footprints, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'SECURITY_OFFICER'], group: 'security' },
  { path: '/security/visitors', label: 'Visitor Log', tKey: 'nav.secVisitors', icon: UserPlus, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'SECURITY_OFFICER'], group: 'security' },
  { path: '/security/maint-visits', label: 'Maintenance Visits', tKey: 'nav.secMaintVisits', icon: Truck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'SECURITY_OFFICER'], group: 'security' },
  { path: '/security/keys', label: 'Key Register', tKey: 'nav.secKeys', icon: KeyRound, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'SECURITY_OFFICER'], group: 'security' },

  // People
  { path: '/staff', label: 'Staff', tKey: 'nav.staff', icon: Users, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'HR_MANAGER'], group: 'people' },
  { path: '/attendance', label: 'Attendance', tKey: 'nav.attendance', icon: ClipboardCheck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ACCOUNTANT', 'HR_MANAGER'], group: 'people' },
  { path: '/tenants', label: 'Tenants', tKey: 'nav.tenants', icon: UserCheck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'], group: 'people', feature: 'tenant_portal' },
  { path: '/hr', label: 'HR', tKey: 'nav.hr', icon: UserCog, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'HR_MANAGER'], group: 'people', feature: 'hr' },

  // Finance — premium add-ons gated by feature flags
  { path: '/accounting', label: 'Accounting', tKey: 'nav.accounting', icon: Wallet, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT', 'MANAGER'], group: 'finance', feature: 'accounting' },
  { path: '/advanced-accounting', label: 'Advanced Accounting', tKey: 'nav.advAccounting', icon: TrendingUp, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT'], group: 'finance', feature: 'accounting_module' },
  { path: '/quotations', label: 'Quotations', tKey: 'nav.quotations', icon: FileSignature, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR', 'PROCUREMENT_OFFICER'], group: 'finance', feature: 'sales_module' },
  { path: '/vouchers', label: 'Vouchers', tKey: 'nav.vouchers', icon: Receipt, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT', 'MANAGER'], group: 'finance', feature: 'accounting_module' },
  { path: '/credit-debit-notes', label: 'Credit / Debit Notes', tKey: 'nav.notes', icon: FileText, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT', 'MANAGER'], group: 'finance', feature: 'accounting_module' },
  { path: '/statements', label: 'Account Statements', tKey: 'nav.statements', icon: BookOpen, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT', 'MANAGER', 'CS_SUPERVISOR'], group: 'finance', feature: 'accounting_module' },
  { path: '/purchases', label: 'Purchases', tKey: 'nav.purchases', icon: ShoppingCart, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'PROCUREMENT_OFFICER', 'STOREKEEPER'], group: 'procurement', feature: 'purchase_module' },  { path: '/purchase-orders', label: 'LPO Preparation', tKey: 'nav.lpo', icon: ClipboardList, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'PROCUREMENT_OFFICER'], group: 'procurement', feature: 'purchase_module' },
  { path: '/procurement', label: 'Delivery & Payments', tKey: 'nav.procurement', icon: Truck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR', 'PROCUREMENT_OFFICER', 'STOREKEEPER'], group: 'procurement', feature: 'purchase_module' },
  { path: '/sales-purchase-dashboard', label: 'Sales/Purchase PnL', tKey: 'nav.spDashboard', icon: BarChart3, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'PROCUREMENT_OFFICER'], group: 'procurement', feature: 'sales_module' },
  { path: '/stock', label: 'Stock', tKey: 'nav.stock', icon: Warehouse, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'ENGINEER', 'STOREKEEPER', 'PROCUREMENT_OFFICER'], group: 'procurement' },
  { path: '/stock-movements', label: 'Stock Audit', tKey: 'nav.stockMovements', icon: Activity, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'STOREKEEPER', 'PROCUREMENT_OFFICER'], group: 'procurement' },
  { path: '/pending-returns', label: 'Pending Returns', tKey: 'nav.pendingReturns', icon: History, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'STOREKEEPER', 'ENGINEER', 'TECHNICIAN'], group: 'procurement' },
  { path: '/suppliers', label: 'Suppliers', tKey: 'nav.suppliers', icon: Package, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'PROCUREMENT_OFFICER', 'STOREKEEPER'], group: 'procurement', feature: 'purchase_module' },
  { path: '/supplier-dashboard', label: 'Supplier Dashboard', tKey: 'nav.supplierDashboard', icon: BarChart3, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'PROCUREMENT_OFFICER'], group: 'procurement', feature: 'purchase_module' },

  // Compliance
  { path: '/amc-contracts', label: 'AMC Contracts', tKey: 'nav.amcContracts', icon: FileBarChart, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'], group: 'compliance', feature: 'amc_contracts' },
  { path: '/provider-kpi', label: 'Provider KPI', tKey: 'nav.providerKpi', icon: BarChart3, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'], group: 'compliance', feature: 'amc_contracts' },
  { path: '/technician-kpi', label: 'Technician KPI', tKey: 'nav.technicianKpi', icon: TrendingUp, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER'], group: 'insights' },
  { path: '/executive', label: 'Executive', tKey: 'nav.executive', icon: TrendingUp, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'], group: 'insights' },
  { path: '/finance', label: 'Finance', tKey: 'nav.finance', icon: FileBarChart, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT'], group: 'insights' },
  { path: '/certificates', label: 'Certificates', tKey: 'nav.certificates', icon: FileCheck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'], group: 'compliance' },
  { path: '/vault', label: 'Document Vault', tKey: 'nav.vault', icon: FolderOpen, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR'], group: 'compliance' },
  { path: '/audit-logs', label: 'Audit Log', tKey: 'nav.auditLogs', icon: History, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'], group: 'compliance' },

  // Insights
  { path: '/analytics', label: 'Analytics', tKey: 'nav.analytics', icon: BarChart3, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'], group: 'insights' },
  { path: '/custom-reports', label: 'Custom Reports', tKey: 'nav.customReports', icon: FileBarChart, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR', 'ENGINEER', 'PROCUREMENT_OFFICER', 'STOREKEEPER'], group: 'insights' },
  { path: '/workflows', label: 'Workflows', tKey: 'nav.workflows', icon: GitBranch, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'ACCOUNTANT'], group: 'insights' },
  { path: '/ai-insights', label: 'AI Insights', tKey: 'nav.aiInsights', icon: Sparkles, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'], group: 'insights', feature: 'ai_insights' },

  // Administration
  { path: '/users', label: 'Users', tKey: 'nav.users', icon: Settings, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'], group: 'admin' },
  { path: '/companies', label: 'Companies', tKey: 'nav.companies', icon: Home, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'], group: 'admin' },
  { path: '/backups', label: 'Backups', tKey: 'nav.backups', icon: Database, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'], group: 'admin' },
  { path: '/role-access', label: 'Role Access', tKey: 'nav.roleAccess', icon: ShieldCheck, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'], group: 'admin' },
  { path: '/subscription', label: 'Subscription', tKey: 'nav.subscription', icon: Crown, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'], group: 'admin' },
  { path: '/help', label: 'User Guide', tKey: 'nav.help', icon: BookOpen, roles: ['*'], group: 'admin' },
  { path: '/settings', label: 'Settings', tKey: 'nav.settings', icon: Settings, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'], group: 'admin' },
  // Feb-2026 — Virtual permission entries. `virtual: true` items never
  // render in the sidebar (they have no route). They exist so admins can
  // toggle sensitive actions (like Force-Close Complaint) from the Roles &
  // Permissions matrix in the same UI they already use for menu access.
  { path: '/actions/complaint-force-close', label: 'Force-Close Complaint (with reason)', tKey: 'nav.forceCloseComplaint', icon: AlertCircle, roles: ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER'], group: 'admin', virtual: true },
];
// Re-export so pages like RoleAccess.js can render/edit permissions without
// diverging from the authoritative menu list. See RoleAccess.js.
export { ALL_MENU_ITEMS };

const Layout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const { company, hasFeature } = useContext(CompanyContext);
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [rolePermissions, setRolePermissions] = useState(null); // null = not loaded, {} = loaded

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    handler();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Load per-company role permissions (if configured by company admin)
  useEffect(() => {
    if (!user || user.role === 'SUPERADMIN' || !user.company_id) {
      setRolePermissions({});
      return;
    }
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND}/api/role-permissions`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const mine = list.find(p => p.role === user.role);
        setRolePermissions(mine && mine.allowed_menu_paths ? { paths: mine.allowed_menu_paths } : {});
      })
      .catch(() => setRolePermissions({}));
  }, [user]);

  const hasAccess = (item) => {
    if (!user) return false;
    // Feature gating (SUPERADMIN bypasses)
    if (item.feature && user.role !== 'SUPERADMIN' && !hasFeature(item.feature)) return false;
    // Default role gating
    const roleOk = item.roles.includes('*') || item.roles.includes(user.role);
    if (!roleOk) return false;
    // Per-company override (if company admin restricted this role's menu)
    if (rolePermissions && rolePermissions.paths) {
      return rolePermissions.paths.includes(item.path);
    }
    return true;
  };

  const filteredMenu = ALL_MENU_ITEMS.filter(m => !m.virtual).filter(hasAccess);

  // Group menu items by group key (preserving order). Unused groups get skipped.
  const groupedMenu = Object.keys(MENU_GROUPS).map(key => ({
    key,
    info: MENU_GROUPS[key],
    items: filteredMenu.filter(i => i.group === key),
  })).filter(g => g.items.length > 0);

  // Bottom nav on mobile: show 4 primary + More
  const bottomNavPrimary = filteredMenu.slice(0, 4);
  const bottomNavRest = filteredMenu.slice(4);

  return (
    <div className="min-h-screen bg-background">
      <TrialBanner />
      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-gradient-to-b from-[#0f1a2b] via-slate-900 to-[#0a1628] text-white transition-all duration-300 z-40 hidden lg:block ${sidebarOpen ? 'w-64' : 'w-20'}`}
      >
        <div className="p-4 border-b border-slate-800 flex items-center justify-between gap-2">
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              <BrandLogo variant="icon" size={42} className="rounded-lg shadow" data-testid="sidebar-brand-full" />
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 leading-none">by SKG<br/>Apps™</div>
            </div>
          ) : (
            <BrandLogo variant="icon" size={40} className="rounded-lg mx-auto" />
          )}
          <button
            data-testid="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 hover:bg-slate-800 rounded-lg transition flex-shrink-0"
          >
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        <nav className="p-3 space-y-2 overflow-y-auto" style={{ height: 'calc(100% - 180px)' }}>
          {groupedMenu.map((g) => (
            <div key={g.key} className="space-y-0.5">
              {sidebarOpen && (
                <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                  <div className="h-[2px] flex-1 rounded" style={{ background: g.info.hex, opacity: 0.55 }} />
                  <span className="text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ color: g.info.hex }}>
                    {(() => { const tx = t(g.info.tKey); return tx === g.info.tKey ? g.info.label : tx; })()}
                  </span>
                </div>
              )}
              {g.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-sm ${
                      isActive
                        ? 'text-slate-900 font-semibold shadow-lg'
                        : 'hover:bg-slate-800/70 text-slate-300 hover:text-white'
                    } ${!sidebarOpen && 'justify-center'}`}
                    style={isActive ? { background: `linear-gradient(135deg, ${g.info.hex} 0%, ${g.info.hex}dd 100%)`, boxShadow: `0 4px 14px ${g.info.hex}66` } : undefined}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" style={isActive ? { color: '#0f172a' } : { color: g.info.hex }} />
                    {sidebarOpen && <span>{(() => { const tx = t(item.tKey); return tx === item.tKey ? item.label : tx; })()}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-800 bg-slate-900/90">
          <div className={`flex items-center ${sidebarOpen ? 'gap-3' : 'flex-col gap-2'}`}>
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full ring-2 ring-amber-400/60" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-slate-900 font-bold text-sm">
                {user?.name?.charAt(0)}
              </div>
            )}
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{user?.name}</p>
                <p className="text-[10px] text-amber-400 truncate uppercase tracking-wide">{t(ROLE_KEYS[user?.role]) || user?.role}</p>
              </div>
            )}
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800 p-2"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3">
        {user?.is_demo && (
          <div data-testid="demo-banner-mobile" className="mb-2 px-3 py-1.5 rounded-lg bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium text-center">
            Demo mode · read-only · <button onClick={logout} className="underline">Sign in to edit</button>
          </div>
        )}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo variant="icon" size={32} className="rounded-lg" />
            {company?.logo_url && (
              <img src={company.logo_url} alt={company.name} data-testid="company-logo-mobile" className="h-8 w-8 object-contain rounded bg-white border border-slate-200 p-0.5" />
            )}
            <div className="leading-tight min-w-0">
              <div className="font-bold text-foreground text-sm truncate">{company?.name || 'FM Flexi Pro'}</div>
              <div className="text-[9px] uppercase tracking-wider text-amber-600 font-semibold truncate" data-testid="logged-in-as-mobile">
                {t('common.loggedInAs') || 'Logged in as'}: {t(ROLE_KEYS[user?.role]) || user?.role}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button data-testid="mobile-scan-btn" onClick={() => navigate('/scan')} variant="ghost" size="sm" className="p-2" aria-label="Scan QR"><Camera className="w-4 h-4 text-emerald-600" /></Button>
            <LanguageSwitcher compact />
            <TaskIconButton compact />
            <NotificationBell />
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-slate-900 font-bold text-xs">
                {user?.name?.charAt(0)}
              </div>
            )}
            <Button data-testid="mobile-logout-btn" onClick={logout} variant="outline" size="sm" className="p-2 border-rose-300 text-rose-600 hover:bg-rose-50 dark:border-rose-500 dark:text-rose-300 dark:hover:bg-rose-900/30 shrink-0" aria-label="Sign out" title="Sign out">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <GlobalSearch compact />
      </div>

      {/* Desktop Top Bar with search */}
      <div className={`hidden lg:flex sticky top-0 z-20 bg-background/90 backdrop-blur-md border-b border-border px-6 py-3 transition-all items-center gap-4 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <div className="flex items-center gap-2 pr-4 border-r border-border flex-shrink-0">
          {company?.logo_url ? (
            <img src={company.logo_url} alt={company.name} data-testid="company-logo-desktop" className="h-9 w-9 object-contain rounded bg-white border border-border p-0.5" />
          ) : (
            <div className="h-9 w-9 rounded bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-slate-900 font-bold text-sm">
              {company?.name?.charAt(0) || 'F'}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-sm font-semibold text-foreground truncate max-w-[200px]">{company?.name || 'FM Flexi Pro'}</div>
            <div className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold" data-testid="logged-in-as-desktop">
              {t('common.loggedInAs') || 'Logged in as'}: {t(ROLE_KEYS[user?.role]) || user?.role}
            </div>
          </div>
        </div>
        <div className="flex-1"><GlobalSearch /></div>
        {user?.is_demo && (
          <div data-testid="demo-banner-desktop" className="px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium">
            Demo mode · read-only · <button onClick={logout} className="underline ml-1">Sign in to edit</button>
          </div>
        )}
        <LanguageSwitcher />
        <ThemeToggle />
        <Button data-testid="desktop-scan-btn" onClick={() => navigate('/scan')} variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700/40"><Camera className="w-4 h-4 mr-1" />Scan</Button>
        <TaskIconButton />
        <NotificationBell />
      </div>

      {/* Main content */}
      <main className={`transition-all duration-300 ${sidebarOpen && !isMobile ? 'lg:ml-64' : 'lg:ml-20'} pb-20 lg:pb-6`}>
        <div className="p-4 sm:p-6 lg:p-8 lg:pt-4">{children}</div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          {bottomNavPrimary.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-lg transition ${
                  isActive ? 'text-amber-600 bg-amber-50' : 'text-slate-500 active:bg-slate-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium truncate max-w-full">{(() => { const tx = t(item.tKey); return tx === item.tKey ? item.label : tx; })()}</span>
              </button>
            );
          })}
          {bottomNavRest.length > 0 && (
            <button
              data-testid="bottom-nav-more"
              onClick={() => setMobileMoreOpen(true)}
              className="flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-lg text-slate-500 active:bg-slate-100"
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] font-medium">{t('nav.more')}</span>
            </button>
          )}
        </div>
      </nav>

      {/* Mobile "More" sheet */}
      {mobileMoreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50 flex items-end" onClick={() => setMobileMoreOpen(false)}>
          <div
            className="w-full bg-white rounded-t-3xl p-6 animate-slide-up max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="text-lg font-bold text-foreground">{t('common.allModules') === 'common.allModules' ? 'All Modules' : t('common.allModules')}</h3>
                <p className="text-xs text-muted-foreground">Role: {t(ROLE_KEYS[user?.role]) || user?.role}</p>
              </div>
              <Button data-testid="close-more-sheet" variant="ghost" size="sm" onClick={() => setMobileMoreOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-1">
              {bottomNavRest.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    data-testid={`more-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    onClick={() => { navigate(item.path); setMobileMoreOpen(false); }}
                    className={`flex items-center gap-3 p-3 rounded-xl text-left transition ${
                      isActive ? 'bg-amber-50 text-amber-700' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${isActive ? 'bg-amber-100' : 'bg-slate-100'}`}>
                      <Icon className={`w-4 h-4 ${isActive ? 'text-amber-600' : 'text-slate-600'}`} />
                    </div>
                    <span className="flex-1 font-medium text-sm text-slate-900">{(() => { const tx = t(item.tKey); return tx === item.tKey ? item.label : tx; })()}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
